import { useState, useEffect, useRef, useCallback } from 'react';
import { LogService, LogLine } from '@/features/LogMonitor/services/logService';

// Chave para armazenar logs no localStorage
const LOGS_STORAGE_KEY = 'prodgy-logs-history';

// Chave para BroadcastChannel usada para comunicação entre abas
const LOGS_CHANNEL_KEY = 'prodgy-logs-channel';
// Tamanho máximo do buffer de logs (para evitar uso excessivo de memória)
const MAX_LOG_LINES = 1000;
// Tempo máximo de inatividade antes de fechar a conexão automaticamente (em ms)
// Definido mas não utilizado no momento, mantido para implementação futura
// const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutos

// Função para carregar logs do localStorage
const loadLogsFromStorage = (): LogLine[] => {
  try {
    const storedLogs = localStorage.getItem(LOGS_STORAGE_KEY);
    if (storedLogs) {
      const parsedLogs = JSON.parse(storedLogs) as LogLine[];
      // Limitar o número de logs carregados para evitar excesso de memória
      return parsedLogs.slice(-MAX_LOG_LINES);
    }
  } catch (error) {
    console.error('[LogMonitor] Erro ao carregar logs do localStorage:', error);
    // Em caso de erro, limpar o localStorage para evitar problemas futuros
    localStorage.removeItem(LOGS_STORAGE_KEY);
  }
  return [];
};

// Função para salvar logs no localStorage
const saveLogsToStorage = (logs: LogLine[]) => {
  try {
    // Limitar o número de logs salvos
    const logsToSave = logs.slice(-MAX_LOG_LINES);
    localStorage.setItem(LOGS_STORAGE_KEY, JSON.stringify(logsToSave));
  } catch (error) {
    console.error('[LogMonitor] Erro ao salvar logs no localStorage:', error);
  }
};

export function useLogMonitor() {
  // Carregar logs do localStorage na inicialização
  const [logLines, setLogLines] = useState<LogLine[]>(() => loadLogsFromStorage());
  const [isStreaming, setIsStreaming] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado que indica se o componente está ativo/visível
  const [, setIsActive] = useState(true);
  
  // Referências
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const inactivityTimerRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isLeaderRef = useRef<boolean>(false); // Indica se esta instância é a líder
  const lastActivityRef = useRef<number>(Date.now());
  // Intenção explícita do usuário de parar o monitoramento — enquanto true, nenhum
  // mecanismo automático (remontagem, visibilitychange, eleição de líder) pode religar
  const userStoppedRef = useRef<boolean>(false);

  // Função para memoizar a criação de BroadcastChannel
  const getOrCreateChannel = useCallback(() => {
    if (!channelRef.current) {
      try {
        channelRef.current = new BroadcastChannel(LOGS_CHANNEL_KEY);
        
        // Configurar handlers de mensagem
        channelRef.current.onmessage = (event: MessageEvent) => {
          const { type, data } = event.data;
          
          if (type === 'leader-check') {
            // Responder apenas se for o líder
            if (isLeaderRef.current) {
              channelRef.current?.postMessage({ type: 'leader-alive', timestamp: Date.now() });
            }
          } else if (type === 'new-log') {
            // Adicionar novo log recebido de outra aba, mas apenas se não for o líder
            // e o usuário não tiver parado o monitoramento nesta aba
            if (!isLeaderRef.current && !userStoppedRef.current) {
              setLogLines(prev => {
                // Limitar o tamanho do buffer
                const newLogs = [...prev, data];
                if (newLogs.length > MAX_LOG_LINES) {
                  return newLogs.slice(-MAX_LOG_LINES);
                }
                return newLogs;
              });
            }
          } else if (type === 'become-leader') {
            // Se ninguém responder em 1 segundo, tornamos esta instância o líder
            // (exceto se o usuário parou o monitoramento nesta aba)
            setTimeout(() => {
              if (!isLeaderRef.current && !userStoppedRef.current) {
                console.log('[LogMonitor] Assumindo posição de líder para SSE');
                isLeaderRef.current = true;
                startStreamingConnection();
              }
            }, 1000);
          }
        };
      } catch (error) {
        console.error('[LogMonitor] Erro ao criar BroadcastChannel:', error);
        // Se não conseguirmos criar o canal, cada instância se torna líder
        isLeaderRef.current = true;
      }
    }
    return channelRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Referência para o timeout de eleição de líder
  const leaderElectionTimeoutRef = useRef<number | null>(null);
  
  // Função para verificar se é o líder
  const electLeader = useCallback(() => {
    // Evitar múltiplas eleições simultâneas
    if (leaderElectionTimeoutRef.current) {
      clearTimeout(leaderElectionTimeoutRef.current);
      leaderElectionTimeoutRef.current = null;
    }
    
    // Se já é líder, não precisa fazer eleição
    if (isLeaderRef.current) {
      console.log('[LogMonitor] Já é líder, não precisa de eleição');
      return;
    }
    
    const channel = getOrCreateChannel();
    if (channel) {
      // Verificar se já existe um líder
      channel.postMessage({ type: 'leader-check', timestamp: Date.now() });
      
      // Após um timeout, se ninguém responder, esta instância se torna o líder
      // (exceto se o usuário parou o monitoramento enquanto o timeout corria)
      leaderElectionTimeoutRef.current = window.setTimeout(() => {
        if (!isLeaderRef.current && !userStoppedRef.current) {
          console.log('[LogMonitor] Tornando-se líder devido a ausência de resposta');
          isLeaderRef.current = true;
          startStreamingConnection();
        }
      }, 1000);
    } else {
      // Se não há canal, esta instância é o líder
      isLeaderRef.current = true;
      startStreamingConnection();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Função para estabelecer conexão SSE com o servidor - simplificada para deixar o navegador gerenciar
  const startStreamingConnection = useCallback(() => {
    // Nunca abrir conexão se o usuário parou o monitoramento explicitamente
    if (userStoppedRef.current) {
      return;
    }

    // Verificar se já temos uma conexão ativa
    if (eventSourceRef.current && eventSourceRef.current.readyState === EventSource.OPEN) {
      console.log('[LogMonitor] Conexão SSE já está ativa');
      setIsStreaming(true);
      setIsReconnecting(false);
      return;
    }
    
    // Apenas o líder deve manter uma conexão SSE
    if (!isLeaderRef.current) {
      console.log('[LogMonitor] Não é o líder, não iniciará conexão SSE');
      return;
    }
    
    // Limpar conexão anterior se existir
    if (eventSourceRef.current) {
      console.log('[LogMonitor] Fechando conexão SSE anterior');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    // Limpar qualquer timer existente
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    
    console.log('[LogMonitor] Criando nova conexão SSE');
    setIsReconnecting(false);
    setError(null);
    setIsStreaming(true);
    
    try {
      // Criar uma única conexão SSE e deixar o navegador gerenciar a reconexão automática
      eventSourceRef.current = LogService.createServerLogStream(
        // Handler para novos logs
        (data) => {
          // Atualizar timestamp de última atividade
          lastActivityRef.current = Date.now();
          
          // Adicionar novo log e salvar no localStorage
          setLogLines(prev => {
            const newLogs = [...prev, data];
            const updatedLogs = newLogs.length > MAX_LOG_LINES ? newLogs.slice(-MAX_LOG_LINES) : newLogs;
            saveLogsToStorage(updatedLogs);
            return updatedLogs;
          });
          
          // Broadcast do log para outras abas
          channelRef.current?.postMessage({ type: 'new-log', data });
        },
        // Handler para conexão estabelecida
        (connectionData) => {
          console.log('[LogMonitor] Conexão SSE estabelecida');
          setIsReconnecting(false);
          setError(null);
          
          // Processar logs iniciais se houver
          if (connectionData.initialLines && Array.isArray(connectionData.initialLines)) {
            setLogLines(lines => {
              const combined = [...lines, ...connectionData.initialLines];
              const updatedLogs = combined.length > MAX_LOG_LINES ? combined.slice(-MAX_LOG_LINES) : combined;
              saveLogsToStorage(updatedLogs);
              return updatedLogs;
            });
          }
        },
        // Handler de erro - apenas exibir mensagem, o navegador gerencia a reconexão
        (_event) => {
          console.log('[LogMonitor] Erro temporário na conexão SSE - o navegador irá reconectar automaticamente');
          setError('Erro temporário na conexão. Reconectando...');
        }
      );
    } catch (error) {
      console.error('[LogMonitor] Erro ao criar conexão SSE:', error);
      setError('Falha ao iniciar o monitoramento de logs');
      setIsStreaming(false);
    }
  }, []);

  // Função pública para iniciar o streaming de logs
  const startStreaming = useCallback(() => {
    console.log('[LogMonitor] Iniciando streaming de logs');
    userStoppedRef.current = false;
    setIsActive(true);
    lastActivityRef.current = Date.now();
    
    // Verificar o estado da conexão atual, se existir
    if (eventSourceRef.current) {
      const state = eventSourceRef.current.readyState;
      console.log(`[LogMonitor] Estado atual da conexão SSE: ${state === 0 ? 'CONNECTING' : state === 1 ? 'OPEN' : 'CLOSED'}`);
      
      // Forçar o fechamento e criação de uma nova conexão - isso ajuda a garantir que
      // a conexão está realmente funcionando
      console.log('[LogMonitor] Fechando conexão existente para criar uma nova');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    // Sempre forçar a criação de uma nova conexão ao iniciar o streaming
    // Isso garante que teremos eventos sendo recebidos
    if (isLeaderRef.current) {
      console.log('[LogMonitor] Iniciando conexão como líder');
      startStreamingConnection();
    } else {
      console.log('[LogMonitor] Iniciando processo de eleição de líder');
      // Forçar nova eleição para garantir que temos um líder ativo
      electLeader();
    }
  }, [electLeader, startStreamingConnection]);

  // Função pública para parar o streaming de logs
  const stopStreaming = useCallback(() => {
    userStoppedRef.current = true;
    setIsActive(false);
    setIsStreaming(false);
    setIsReconnecting(false);
    
    // Limpar timers
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    
    if (leaderElectionTimeoutRef.current) {
      clearTimeout(leaderElectionTimeoutRef.current);
      leaderElectionTimeoutRef.current = null;
    }
    
    // Fechar conexão SSE
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    // Se for líder, notificar outras abas para eleger novo líder
    if (isLeaderRef.current) {
      isLeaderRef.current = false;
      if (channelRef.current) {
        channelRef.current.postMessage({ type: 'become-leader', timestamp: Date.now() });
      }
    }
  }, []);

  // Limpar recursos quando o componente for desmontado
  useEffect(() => {
    // Criar canal de comunicação entre abas
    getOrCreateChannel();
    
    // Iniciar processo de eleição de líder
    electLeader();
    
    // Configurar detector de visibilidade da página
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Página não está visível - apenas marcar como inativo mas MANTER a conexão
        console.log('[LogMonitor] Página em segundo plano, mantendo conexão SSE ativa');
        setIsActive(false);
        
        // Importante: manter a conexão ativa mesmo quando a aba não está visível
        // para continuar recebendo logs
      } else {
        // Página está visível novamente
        console.log('[LogMonitor] Página visível novamente, verificando estado da conexão');
        setIsActive(true);
        lastActivityRef.current = Date.now();

        // Respeitar o stop explícito do usuário — não religar automaticamente
        if (userStoppedRef.current) {
          return;
        }

        // Verificar se a conexão está ativa, senão reiniciar
        if (eventSourceRef.current) {
          const state = eventSourceRef.current.readyState;
          console.log(`[LogMonitor] Estado atual da conexão SSE: ${state === 0 ? 'CONNECTING' : state === 1 ? 'OPEN' : 'CLOSED'}`);
          
          // Se a conexão estiver fechada, forçar nova conexão
          if (state === EventSource.CLOSED) {
            console.log('[LogMonitor] Conexão fechada, reiniciando conexão...');
            startStreaming();
          }
        } else if (!isLeaderRef.current) {
          console.log('[LogMonitor] Sem conexão ativa, reiniciando processo de eleição');
          electLeader();
        } else {
          console.log('[LogMonitor] Reiniciando streaming como líder');
          startStreamingConnection();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Iniciar streaming automaticamente na montagem, exceto se o usuário
    // parou o monitoramento explicitamente
    if (!userStoppedRef.current) {
      startStreaming();
    }
    
    // Limpar recursos quando o componente desmontar
    return () => {
      console.log('[LogMonitor] Desmontando componente, limpando recursos');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Fechar conexão SSE
      if (eventSourceRef.current) {
        console.log('[LogMonitor] Fechando conexão SSE na desmontagem');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      
      // Limpar timers
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      
      if (leaderElectionTimeoutRef.current) {
        console.log('[LogMonitor] Limpando timeout de eleição de líder');
        clearTimeout(leaderElectionTimeoutRef.current);
        leaderElectionTimeoutRef.current = null;
      }
      
      // Fechar canal de comunicação
      if (channelRef.current) {
        console.log('[LogMonitor] Fechando canal de comunicação');
        channelRef.current.close();
        channelRef.current = null;
      }
      
      // Desistir da liderança
      if (isLeaderRef.current) {
        console.log('[LogMonitor] Desistindo da liderança na desmontagem');
        isLeaderRef.current = false;
      }
    };
  // Efeito só de montagem/desmontagem: com isStreaming nas deps, o stop do usuário
  // re-executava o efeito e o auto-start religava o monitoramento (bug 83940)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    logLines,
    isStreaming,
    isReconnecting,
    startStreaming,
    stopStreaming,
    error,
    clearLogs: useCallback(() => setLogLines([]), [])
  };
}
